import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { getAccessToken } from '../store/actions/auth';
import Loading from '../components/Loading';
import API_BASE_URL from '../config/baseUrl';

// Left Arrow Icon
const LeftArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
  </svg>
);

// Right Arrow Icon
const RightArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
  </svg>
);

// Fetch login report data for logged-in user
const fetchLoginReport = async function(userId) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/ant-pro-login-report",
      { userId: userId },
      { headers: { Authorization: "Bearer " + token } }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to fetch login report" };
    }

    return { success: true, data: data.data || [] };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to fetch login report";
    return { success: false, error: errorMessage };
  }
};

// Format date for display (17-01-2026 05:32 PM)
const formatDisplayDate = function(dateString) {
  if (!dateString) return '-';
  
  // Handle the format "2026-01-13 11:39:17" from API
  let date;
  if (dateString.includes(' ') && !dateString.includes('T')) {
    // Format: "2026-01-13 11:39:17"
    const [datePart, timePart] = dateString.split(' ');
    date = new Date(datePart + 'T' + timePart);
  } else {
    date = new Date(dateString);
  }
  
  if (isNaN(date.getTime())) return '-';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, '0');
  
  return day + '-' + month + '-' + year + ' ' + hoursStr + ':' + minutes + ' ' + ampm;
};

const ReportsLoginReportPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector(function(state) { return state.auth; });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginReports, setLoginReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Fetch data using logged-in user's ID
  const loadData = async function() {
    if (!user?.userId) {
      setError("User ID not found");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await fetchLoginReport(user.userId);

    if (result.success) {
      // Sort by loginDate descending (most recent first)
      const sortedData = (result.data || []).sort(function(a, b) {
        const dateA = new Date(a.loginDate || a.lastActivity);
        const dateB = new Date(b.loginDate || b.lastActivity);
        return dateB - dateA;
      });
      setLoginReports(sortedData);
    } else {
      setError(result.error);
      setLoginReports([]);
    }

    setLoading(false);
  };

  // Initial load
  useEffect(function() {
    loadData();
  }, [user]);

  // Pagination
  const totalPages = Math.ceil(loginReports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReports = loginReports.slice(startIndex, endIndex);

  const handlePageChange = function(page) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = function() {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      for (let i = 1; i <= maxVisiblePages; i++) {
        pages.push(i);
      }
    } else if (currentPage >= totalPages - 2) {
      for (let i = totalPages - maxVisiblePages + 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-8xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-gray-600 px-5 py-3 flex items-center justify-between">
              <span className="text-2xl text-white font-normal capitalize">
                Login Report
              </span>
              <button 
                onClick={function() { navigate(-1); }}
                className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto w-full">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error}</div>
              ) : (
                <table className="w-full border-collapse text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border border-gray-300 text-left font-medium">User Name</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">City</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">REGION</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">ISP</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">IP-ADDRESS</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">LOGIN DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentReports.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-gray-500 bg-gray-50">
                          No login reports found
                        </td>
                      </tr>
                    ) : (
                      currentReports.map(function(report, index) {
                        const rowBgClass = index % 2 === 0 ? '' : 'bg-gray-50';
                        
                        return (
                          <tr key={report._id || index} className={rowBgClass + ' hover:bg-gray-100'}>
                            <td className="p-2 border border-gray-300">
                              {report.userId || report.userName || '-'}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.city || ''}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.region || ''}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.isp || ''}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.ipAddress || ''}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {formatDisplayDate(report.loginDate)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {loginReports.length > itemsPerPage && (
              <div className="flex justify-end p-3 mx-3">
                <ul className="flex items-center gap-1">
                  <li>
                    <button
                      onClick={function() { handlePageChange(currentPage - 1); }}
                      disabled={currentPage === 1}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <LeftArrowIcon />
                    </button>
                  </li>
                  {getPageNumbers().map(function(pageNum) {
                    return (
                      <li key={pageNum}>
                        <button
                          onClick={function() { handlePageChange(pageNum); }}
                          className={
                            'px-3 py-1 rounded ' +
                            (currentPage === pageNum 
                              ? 'bg-blue-500 text-white' 
                              : 'border border-gray-300 hover:bg-gray-100')
                          }
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li>
                    <button
                      onClick={function() { handlePageChange(currentPage + 1); }}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <RightArrowIcon />
                    </button>
                  </li>
                </ul>
              </div>
            )}

            {/* Total Records Info */}
            {!loading && !error && (
              <div className="px-4 py-2 text-sm text-gray-600 border-t border-gray-200">
                Total Records: {loginReports.length}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsLoginReportPage;